import type { CreasePattern } from '@/origami/types';
import type { EngineParams, SimulationFrame } from '@/simulation/engine';
import type { WorkerRequest, WorkerResponse } from './protocol';

/**
 * Main-thread handle on the simulation Worker.
 *
 * The contract that keeps the UI at 60 fps: at most one `step` request is in
 * flight at a time. If the render loop asks for another frame while the solver
 * is still busy, the request is dropped rather than queued, so a slow solver
 * degrades the simulation rate and never the interface.
 */
export class SimulationClient {
  private worker: Worker;
  private nextId = 1;
  private inFlight = false;
  private onFrameCb: ((frame: SimulationFrame) => void) | null = null;
  private onErrorCb: ((message: string) => void) | null = null;
  private onLoadedCb: ((warnings: string[]) => void) | null = null;
  /** Wall-clock milliseconds of the last round trip. */
  lastRoundTripMs = 0;
  private lastSentAt = 0;

  constructor() {
    this.worker = new Worker(new URL('./simulation.worker.ts', import.meta.url), {
      type: 'module',
    });
    this.worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const msg = event.data;
      switch (msg.type) {
        case 'frame':
          this.inFlight = false;
          this.lastRoundTripMs = performance.now() - this.lastSentAt;
          this.onFrameCb?.(msg.frame);
          break;
        case 'loaded':
          this.onLoadedCb?.(msg.warnings);
          break;
        case 'error':
          this.inFlight = false;
          this.onErrorCb?.(msg.message);
          break;
        case 'pong':
          break;
      }
    };
    this.worker.onerror = (e) => {
      this.inFlight = false;
      this.onErrorCb?.(e.message);
    };
  }

  onFrame(cb: (frame: SimulationFrame) => void): void {
    this.onFrameCb = cb;
  }
  onError(cb: (message: string) => void): void {
    this.onErrorCb = cb;
  }
  onLoaded(cb: (warnings: string[]) => void): void {
    this.onLoadedCb = cb;
  }

  private send(msg: WorkerRequest): void {
    this.worker.postMessage(msg);
  }

  load(pattern: CreasePattern): void {
    this.inFlight = false;
    this.send({ type: 'load', id: this.nextId++, pattern });
  }

  reset(): void {
    this.send({ type: 'reset', id: this.nextId++ });
  }

  /** Returns false when the worker is still busy and the request was skipped. */
  requestStep(params: EngineParams, steps = 1): boolean {
    if (this.inFlight) return false;
    this.inFlight = true;
    this.lastSentAt = performance.now();
    this.send({ type: 'step', id: this.nextId++, params, steps });
    return true;
  }

  get busy(): boolean {
    return this.inFlight;
  }

  dispose(): void {
    this.worker.terminate();
  }
}
